import { useEffect, useRef, useState } from "react";
import { loadGoogleMaps } from "../lib/googleMaps";

/**
 * Address Line 1 input with Google Places Autocomplete.
 * On selection, calls onPlace({ line1, city, state, zip }).
 *
 * Falls back to a plain input if the Maps API key is missing or fails to load.
 */
export default function AddressAutocomplete({
  value, onChange, onPlace, placeholder = "123 Main St", testId = "checkout-address1",
}) {
  const inputRef = useRef(null);
  const acRef = useRef(null);
  const [enabled, setEnabled] = useState(false);

  useEffect(() => {
    let cancelled = false;
    loadGoogleMaps()
      .then((google) => {
        if (cancelled || !inputRef.current || acRef.current) return;
        const ac = new google.maps.places.Autocomplete(inputRef.current, {
          types: ["address"],
          componentRestrictions: { country: ["us"] },
          fields: ["address_components", "formatted_address"],
        });
        ac.addListener("place_changed", () => {
          const p = ac.getPlace();
          if (!p?.address_components) return;
          const comps = p.address_components;
          const get = (type) => comps.find((c) => c.types.includes(type));
          const street_number = get("street_number")?.long_name || "";
          const route = get("route")?.long_name || "";
          const line1 = [street_number, route].filter(Boolean).join(" ").trim() || p.formatted_address?.split(",")[0] || "";
          const city = get("locality")?.long_name || get("sublocality")?.long_name || get("postal_town")?.long_name || "";
          const stateCode = get("administrative_area_level_1")?.short_name || "";
          const zip = get("postal_code")?.long_name || "";
          onPlace?.({ line1, city, state: stateCode, zip });
        });
        acRef.current = ac;
        setEnabled(true);
      })
      .catch(() => setEnabled(false));
    return () => {
      cancelled = true;
    };
  }, [onPlace]);

  return (
    <input
      ref={inputRef}
      data-testid={testId}
      data-autocomplete-enabled={enabled}
      type="text"
      autoComplete="address-line1"
      className="rounded-input mt-1"
      value={value}
      onChange={(e) => onChange(e.target.value)}
      placeholder={placeholder}
    />
  );
}
